import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Redirect, Route, RouteProps, Switch, useLocation } from 'react-router-dom';
import SearchPage from './pages/SearchPage/SearchPage';
import ResultPage from './pages/ResultPage/ResultPage';
import DetailPage from './pages/DetailPage/DetailPage';
import Navbar from './components/Navbar/Navbar';
import ExplanationPage from './pages/ExplanationPage/ExplanationPage';
import LoginPage from './pages/Login/LoginPage/LoginPage';
import LoginPinPage from './pages/Login/LoginPinPage/LoginPinPage';
import AutoLogin from './components/AutoLogin/AutoLogin';
import { useService } from 'react-service-locator';
import { AccountService } from './services/account.service';
import SettingsPage from './pages/AdminBoard/SettingsPage/SettingsPage';
import MasterUserDashboard from './pages/AdminBoard/SettingsPage/UsersDashboard/MasterUserDashboard';
import { useGetRoles, useIsMaster, useIsUserAnyTypeOfAdmin } from './hooks/UserHooks';
import RegisterNewClient from './pages/AdminBoard/SettingsPage/RegisterNewClient/RegisterNewClient';
import { observer } from 'mobx-react-lite';
import { Toaster } from 'sonner';
import RegisterMasterUser from './pages/AdminBoard/SettingsPage/RegisterMasterUser/RegisterMasterUser';
import { initOnRefresh } from './axios/axiosAuthInterceptor';
import LicensePage from './pages/LicensePage/LicensePage';
import SignupPage from './pages/SignUpPage/SignUpPage';
import SignupSuccess from './pages/SignUpSuccessPage/SignUpSuccessPage';
import TeamPage from './pages/TeamPage/TeamPage';
import SubscriptionPage from './pages/SubscriptionPage/SubscriptionPage';
import Footer from './components/Footer/Footer';
import EditDemoUser from './pages/AdminBoard/SettingsPage/UsersDashboard/EditDemoUser/EditDemoUser';

const isAuthenticatedFirst = () => {
    const accessToken = localStorage.getItem('refreshToken');
    return !!accessToken;
};

interface PrivateRouteProps extends RouteProps {
    component: React.ComponentType<any>;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ component: Component, ...rest }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(isAuthenticatedFirst);
    const accountService = useService(AccountService);

    const email = localStorage.getItem('UserEmail');
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                if (email) {
                    await accountService.fetchAccountSettings(email);
                } else {
                    setIsAuthenticated(false);
                }
                setIsAuthenticated(true);
            } catch (error) {
                setIsAuthenticated(false);
            }
        };

        fetchSettings(); // Выполняем запрос при монтировании компонента
    }, [email]);
    return (
        <Route {...rest} render={(props) => (isAuthenticated ? <Component {...props} /> : <Redirect to="/login" />)} />
    );
};

const MasterPrivateRoute: React.FC<PrivateRouteProps> = ({ component: Component, ...rest }) => {
    const [isMasterUser, setIsMasterUser] = useState(useGetRoles()?.includes('MASTER') ?? false);

    useEffect(() => {
        const fetchRoles = async () => {
            const roles = await useGetRoles();
            const isAllowed = roles ? roles.includes('MASTER') : false;
            setIsMasterUser(isAllowed);
        };

        fetchRoles();
    }, []);
    return (
        <Route
            {...rest}
            render={(props) => (isMasterUser ? <PrivateRoute {...props} component={Component} /> : <Redirect to="/" />)}
        />
    );
};

const AnyAdminPrivateRoute: React.FC<PrivateRouteProps> = ({ component: Component, ...rest }) => {
    const [isMasterUser, setIsMasterUser] = useState(useIsMaster());

    useEffect(() => {
        setIsMasterUser(useIsMaster());
    }, []);
    return (
        <Route
            {...rest}
            render={(props) => (isMasterUser ? <PrivateRoute {...props} component={Component} /> : <Redirect to="/" />)}
        />
    );
};

declare global {
    interface Window {
        _mtm?: any[];
    }
}

const App: React.FC = observer(() => {
    initOnRefresh();

    useEffect(() => {
        const _mtm = (window._mtm = window._mtm || []);
        _mtm.push({
            'mtm.startTime': new Date().getTime(),
            event: 'mtm.Start',
        });

        const d = document;
        const g = d.createElement('script');
        const s = d.getElementsByTagName('script')[0];

        g.async = true;
        g.src = 'https://cdn.matomo.cloud/lifesight.matomo.cloud/container_FRWvtXWT.js';
        s.parentNode?.insertBefore(g, s);
    }, []);

    return (
        <>
            <Router>
                <Toaster richColors={true} position="top-center" />
                <Navbar />
                <Switch>
                    <PrivateRoute exact path="/" component={SearchPage} />
                    <PrivateRoute exact path="/explanation" component={ExplanationPage} />
                    <PrivateRoute exact path="/results" component={ResultPage} />
                    <PrivateRoute path="/results/:id" component={DetailPage} />
                    <Route exact path="/login" component={LoginPage} />
                    <Route exact path="/login-pin" component={LoginPinPage} />
                    <Route exact path="/autoLogin" component={AutoLogin} />
                    <Route exact path="/license" component={LicensePage} />
                    <Route exact path="/signup" component={SignupPage} />
                    <Route exact path="/signup/success" component={SignupSuccess} />
                    <Route exact path="/team" component={TeamPage} />
                    <Route exact path="/subscription" component={SubscriptionPage} />
                    <MasterPrivateRoute exact path="/admin-settings/register-client" component={RegisterNewClient} />
                    <MasterPrivateRoute
                        exact
                        path="/admin-settings/register-master-user"
                        component={RegisterMasterUser}
                    />
                    <AnyAdminPrivateRoute exact path="/admin-settings" component={SettingsPage} />
                    <AnyAdminPrivateRoute exact path="/admin-settings/dashboard" component={MasterUserDashboard} />
                    <AnyAdminPrivateRoute path="/admin-settings/dashboard/demo/:id/edit" component={EditDemoUser} />
                </Switch>
                <Footer externalUrl="https://lifesight.org" />
            </Router>
        </>
    );
});

export default App;
